/**
 * @file advanced-color-coordinator.ts
 * @description Manages the Advanced Color Mixer UI (RGBA/HEX).
 * Part of #2074-AdvancedColor.
 */

import { g } from '@hcie/core';
import { EventBus } from '@hcie/shared';

export function initAdvancedColorCoordinator(): void {
    console.log("[Advanced-Color-Coordinator] Initializing mixer logic...");

    // Expose functions to window for Modal actions
    (window as any).showAdvancedColorPicker = showAdvancedColorPicker;
    (window as any).closeAdvancedColorPicker = closeAdvancedColorPicker;
    (window as any).applyAdvancedColor = applyAdvancedColor;

    setupListeners();
}

function setupListeners(): void {
    const ids = ['Red', 'Green', 'Blue', 'Alpha'];
    
    ids.forEach(channel => {
        const range = document.getElementById(`adv${channel}Range`) as HTMLInputElement;
        const input = document.getElementById(`adv${channel}Input`) as HTMLInputElement;
        
        if (range && input) {
            range.addEventListener('input', () => {
                input.value = range.value;
                updatePreview();
            });
            input.addEventListener('input', () => {
                range.value = input.value;
                updatePreview();
            });
        }
    });

    const hexInput = document.getElementById('advHexInput') as HTMLInputElement;
    if (hexInput) {
        hexInput.addEventListener('input', () => {
            if (isValidHex(hexInput.value)) {
                syncFromHex(hexInput.value);
                updatePreview();
            }
        });
    }
}

function showAdvancedColorPicker(): void {
    const modal = document.getElementById('advancedColorModal');
    if (modal) {
        modal.style.display = 'flex';
        
        // Load current global color into picker
        syncFromHex(g.pen_color);
        
        // Load current opacity (pen_opacity is 0-1, mixer uses 0-100)
        const alphaRange = document.getElementById('advAlphaRange') as HTMLInputElement;
        const alphaInput = document.getElementById('advAlphaInput') as HTMLInputElement;
        if (alphaRange && alphaInput) {
            const opacityVal = Math.round((g.pen_opacity ?? 1) * 100);
            alphaRange.value = opacityVal.toString();
            alphaInput.value = opacityVal.toString();
        }
        
        updatePreview();
    }
}

function closeAdvancedColorPicker(): void {
    const modal = document.getElementById('advancedColorModal');
    if (modal) {
        modal.style.display = 'none';
    }
}

function applyAdvancedColor(): void {
    const r = (document.getElementById('advRedInput') as HTMLInputElement).value;
    const gVal = (document.getElementById('advGreenInput') as HTMLInputElement).value;
    const b = (document.getElementById('advBlueInput') as HTMLInputElement).value;
    const a = (document.getElementById('advAlphaInput') as HTMLInputElement).value;

    const hex = rgbToHex(parseInt(r), parseInt(gVal), parseInt(b));
    const alphaNum = parseInt(a) / 100;

    // Update global state
    g.pen_color = hex;
    g.pen_opacity = alphaNum;
    
    // Add to recent colors (Global function from colorbox.ts)
    if ((window as any).addColorToRecent) {
        (window as any).addColorToRecent(hex);
    }
    
    // Notify UI that settings changed (Properties panel, mobile header icons etc)
    EventBus.emit('UPDATE_LAYER_PANEL'); // Triggers generic UI refresh
    EventBus.emit('RENDER_LAYERS');

    window.dispatchEvent(new CustomEvent('colorChanged', { 
        detail: { 
            color: hex, 
            opacity: alphaNum,
            source: 'advanced-mixer' 
        } 
    }));
    
    // Auto-close panels on mobile (#2076-UX)
    EventBus.emit('COLOR_CHANGED', { color: hex });

    closeAdvancedColorPicker();
}

function updatePreview(): void {
    const preview = document.getElementById('advColorPreview');
    const r = (document.getElementById('advRedInput') as HTMLInputElement).value;
    const gVal = (document.getElementById('advGreenInput') as HTMLInputElement).value;
    const b = (document.getElementById('advBlueInput') as HTMLInputElement).value;
    const a = (document.getElementById('advAlphaInput') as HTMLInputElement).value;
    const hexInput = document.getElementById('advHexInput') as HTMLInputElement;

    const colorStr = `rgba(${r}, ${gVal}, ${b}, ${parseInt(a) / 100})`;
    if (preview) preview.style.backgroundColor = colorStr;
    
    // Update hex text without triggering listeners
    if (hexInput && document.activeElement !== hexInput) {
        hexInput.value = rgbToHex(parseInt(r), parseInt(gVal), parseInt(b));
    }
}

function syncFromHex(hex: string): void {
    const result = hexToRgb(hex);
    if (result) {
        (document.getElementById('advRedRange') as HTMLInputElement).value = result.r.toString();
        (document.getElementById('advRedInput') as HTMLInputElement).value = result.r.toString();
        (document.getElementById('advGreenRange') as HTMLInputElement).value = result.g.toString();
        (document.getElementById('advGreenInput') as HTMLInputElement).value = result.g.toString();
        (document.getElementById('advBlueRange') as HTMLInputElement).value = result.b.toString();
        (document.getElementById('advBlueInput') as HTMLInputElement).value = result.b.toString();
        
        const hexInput = document.getElementById('advHexInput') as HTMLInputElement;
        if (hexInput) hexInput.value = hex.toUpperCase();
    }
}

function isValidHex(hex: string): boolean {
    return /^#([A-Fa-f0-9]{3}){1,2}$/.test(hex);
}

function hexToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
}

function rgbToHex(r: number, g: number, b: number): string {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
}
